import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  OrgCategory,
  ORG_CATEGORY_LABELS,
  Organization,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

interface CreateForm {
  name: string;
  slug: string;
  category: OrgCategory | "";
}

const EMPTY_CREATE: CreateForm = { name: "", slug: "", category: "" };

interface EditForm {
  name: string;
  slug: string;
  category: OrgCategory | "";
}

export default function AdminOrganizations() {
  const me = useAuth((s) => s.user);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isOwner = me?.role === "owner";

  useEffect(() => {
    if (!isOwner) return;
    void load();
  }, [isOwner]);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const list = await api.get<Organization[]>("/organizations");
      setOrgs(list);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load organizations");
    } finally {
      setBusy(false);
    }
  }

  function autoSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 128);
  }

  function openEdit(org: Organization) {
    setEditingId(org.id);
    setEditForm({ name: org.name, slug: org.slug, category: org.category ?? "" });
  }

  function closeEdit() {
    setEditingId(null);
    setEditForm(null);
  }

  async function submitCreate(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    setCreating(true);
    try {
      const created = await api.post<Organization>("/organizations", {
        name: createForm.name.trim(),
        slug: createForm.slug.trim(),
        category: createForm.category || null,
      });
      setOrgs((cur) =>
        [...cur, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setCreateForm(EMPTY_CREATE);
      setShowCreate(false);
      flashFor(`Created "${created.name}".`);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to create organization");
    } finally {
      setCreating(false);
    }
  }

  async function submitEdit(ev: FormEvent) {
    ev.preventDefault();
    if (editingId == null || !editForm) return;
    setErr(null);
    setSavingEdit(true);
    try {
      const updated = await api.patch<Organization>(
        `/organizations/${editingId}`,
        {
          name: editForm.name.trim(),
          slug: editForm.slug.trim(),
          category: editForm.category || null,
        },
      );
      setOrgs((cur) =>
        cur
          .map((o) => (o.id === updated.id ? updated : o))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      flashFor(`Updated "${updated.name}".`);
      closeEdit();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to update organization");
    } finally {
      setSavingEdit(false);
    }
  }

  async function confirmDelete() {
    if (deleteId == null) return;
    setErr(null);
    setDeleting(true);
    try {
      await api.del(`/organizations/${deleteId}`);
      const removed = orgs.find((o) => o.id === deleteId);
      setOrgs((cur) => cur.filter((o) => o.id !== deleteId));
      setDeleteId(null);
      flashFor(`Deleted "${removed?.name ?? "organization"}".`);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to delete organization");
    } finally {
      setDeleting(false);
    }
  }

  function flashFor(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash((m) => (m === msg ? null : m)), 3500);
  }

  const sortedOrgs = useMemo(
    () => [...orgs].sort((a, b) => a.name.localeCompare(b.name)),
    [orgs],
  );

  if (!isOwner) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Organizations</div>
            <h1>Organizations</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Organization management is restricted to owners.
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Site settings</div>
          <h1>Organizations</h1>
          <p className="admin__lead">
            Create and manage tenant organizations. Each organization has its
            own users, cases, and data.
          </p>
        </div>
        <div className="admin__head-actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setShowCreate((s) => !s);
              setErr(null);
            }}
          >
            {showCreate ? "Cancel" : "+ New organization"}
          </button>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}
      {flash && <div className="alert alert--ok">{flash}</div>}

      {showCreate && (
        <form className="admin-card" onSubmit={submitCreate}>
          <div className="admin-card__head">
            <div>
              <h2 className="admin-card__title">New organization</h2>
              <p className="admin-card__blurb">
                The slug is used in URLs and must be unique. It can only contain
                lowercase letters, numbers, and hyphens.
              </p>
            </div>
          </div>
          <div className="admin-card__grid">
            <div className="admin-card__field admin-card__field--wide">
              <label htmlFor="co-name">Organization name</label>
              <input
                id="co-name"
                type="text"
                required
                autoComplete="off"
                value={createForm.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setCreateForm((f) => ({
                    ...f,
                    name,
                    slug: f.slug === autoSlug(f.name) ? autoSlug(name) : f.slug,
                  }));
                }}
              />
            </div>
            <div className="admin-card__field admin-card__field--wide">
              <label htmlFor="co-slug">Slug</label>
              <input
                id="co-slug"
                type="text"
                required
                autoComplete="off"
                pattern="^[a-z0-9][a-z0-9\-]*$"
                value={createForm.slug}
                onChange={(e) =>
                  setCreateForm({ ...createForm, slug: e.target.value })
                }
              />
              <div className="admin-card__hint muted">
                Lowercase letters, numbers, and hyphens only.
              </div>
            </div>
            <div className="admin-card__field admin-card__field--wide">
              <label htmlFor="co-category">Category</label>
              <select
                id="co-category"
                value={createForm.category}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    category: (e.target.value as OrgCategory) || "",
                  })
                }
              >
                <option value="">— none —</option>
                {Object.entries(ORG_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <div className="admin-card__hint muted">
                Industry vertical for this organization.
              </div>
            </div>
          </div>
          <div className="admin-card__foot">
            <div className="admin-card__meta muted">
              You can add users to this organization after creating it.
            </div>
            <div className="admin-card__actions">
              <button
                type="button"
                className="btn"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={creating}
              >
                {creating ? "Creating…" : "Create organization"}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Category</th>
              <th>Users</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr>
                <td colSpan={6} className="muted">
                  Loading…
                </td>
              </tr>
            )}
            {!busy && sortedOrgs.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No organizations yet.
                </td>
              </tr>
            )}
            {sortedOrgs.map((org) => {
              const isMine = org.id === me?.organization_id;
              return (
                <tr key={org.id}>
                  <td>
                    <div className="admin-table__name">
                      {org.name}
                      {isMine && (
                        <span className="admin-table__chip admin-table__chip--me">
                          yours
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="admin-table__mono">{org.slug}</td>
                  <td>
                    {org.category
                      ? ORG_CATEGORY_LABELS[org.category] ?? org.category
                      : <span className="muted">—</span>}
                  </td>
                  <td>{org.user_count}</td>
                  <td className="muted">
                    {new Date(org.created_at).toLocaleDateString()}
                  </td>
                  <td className="admin-table__actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => openEdit(org)}
                    >
                      Edit
                    </button>
                    {!isMine && (
                      <button
                        type="button"
                        className="btn btn--danger"
                        onClick={() => setDeleteId(org.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editingId != null && editForm && (
        <div
          className="admin-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeEdit();
          }}
        >
          <form className="admin-modal" onSubmit={submitEdit}>
            <header className="admin-modal__head">
              <h2>Edit organization</h2>
              <button
                type="button"
                className="admin-modal__close"
                onClick={closeEdit}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <div className="admin-card__grid">
              <div className="admin-card__field admin-card__field--wide">
                <label htmlFor="eo-name">Organization name</label>
                <input
                  id="eo-name"
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                />
              </div>
              <div className="admin-card__field admin-card__field--wide">
                <label htmlFor="eo-slug">Slug</label>
                <input
                  id="eo-slug"
                  type="text"
                  required
                  pattern="^[a-z0-9][a-z0-9\-]*$"
                  value={editForm.slug}
                  onChange={(e) =>
                    setEditForm({ ...editForm, slug: e.target.value })
                  }
                />
                <div className="admin-card__hint muted">
                  Lowercase letters, numbers, and hyphens only.
                </div>
              </div>
              <div className="admin-card__field admin-card__field--wide">
                <label htmlFor="eo-category">Category</label>
                <select
                  id="eo-category"
                  value={editForm.category}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      category: (e.target.value as OrgCategory) || "",
                    })
                  }
                >
                  <option value="">— none —</option>
                  {Object.entries(ORG_CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <div className="admin-card__hint muted">
                  Industry vertical for this organization.
                </div>
              </div>
            </div>

            <footer className="admin-modal__foot">
              <button
                type="button"
                className="btn"
                onClick={closeEdit}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={savingEdit}
              >
                {savingEdit ? "Saving…" : "Save changes"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteId != null && (
        <div
          className="admin-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDeleteId(null);
          }}
        >
          <div className="admin-modal">
            <header className="admin-modal__head">
              <h2>Delete organization</h2>
              <button
                type="button"
                className="admin-modal__close"
                onClick={() => setDeleteId(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <div style={{ padding: "1rem 1.5rem" }}>
              <p>
                Are you sure you want to delete{" "}
                <strong>{orgs.find((o) => o.id === deleteId)?.name}</strong>?
                This will permanently remove the organization and all of its
                users, cases, and data. This action cannot be undone.
              </p>
            </div>

            <footer className="admin-modal__foot">
              <button
                type="button"
                className="btn"
                onClick={() => setDeleteId(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
